import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Mount inside a router subtree to manage scroll on navigation.
 *
 * <ul>
 *   <li>No hash → scroll to the top of the page (React Router otherwise
 *       preserves the previous scroll position, which is wrong for
 *       content-style sites).</li>
 *   <li>Hash present → wait one tick for the new route to mount, then
 *       scroll to the element with that id (with smooth behaviour).
 *       Falls back to top if the id isn't in the DOM.</li>
 * </ul>
 *
 * Pair anchor targets with {@code scroll-mt-*} utilities so the sticky
 * top-nav doesn't cover them.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Strip the leading '#' and try to find the element. We defer with
      // requestAnimationFrame so the new route's components have a chance
      // to render before the lookup runs.
      const id = hash.slice(1);
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
      };
      requestAnimationFrame(tryScroll);
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}
